import { memo, useState } from "react"

import { AlertTriangle, Check, Copy, FileText, Image as ImageIcon, Info, Loader2, Sparkles } from "lucide-react"

import { Markdown } from "@/components/chat/Markdown"
import { SourceList } from "@/components/chat/SourceList"
import { SuggestionChips } from "@/components/chat/SuggestionChips"
import type { ChatMessage } from "@/hooks/useChatStream"
import { useI18n } from "@/i18n"
import { retrievalEngineLabel } from "@/lib/text"
import { cn } from "@/lib/utils"

function GeneratedImage({ url, prompt }: { url: string; prompt: string }) {
  const { t } = useI18n()
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("message.imageFailed")}{" "}
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-orange-press hover:underline">
          {t("message.openImage")}
        </a>
      </p>
    )
  }
  return (
    <div>
      <img
        src={url}
        alt={prompt || t("message.generatedImage")}
        title={t("message.openFullSize")}
        className="block max-w-full cursor-pointer rounded-xl"
        onClick={() => window.open(url, "_blank")}
        onError={() => setFailed(true)}
      />
      {prompt && <p className="mt-1.5 text-xs text-muted-foreground italic">{prompt}</p>}
    </div>
  )
}

/** Copy-to-clipboard action, styled as the celver answer-action button. */
function CopyButton({ text }: { text: string }) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={t("message.copyAnswer")}
      title={copied ? t("message.copied") : t("message.copyAnswer")}
      className="flex size-[30px] items-center justify-center rounded-md border border-border-subtle text-fg3 transition-colors hover:border-border hover:text-foreground"
    >
      {copied ? <Check className="size-3.5 text-signal-text" /> : <Copy className="size-3.5" />}
    </button>
  )
}

function AssistantBody({ message, onSuggestionSelect }: MessageBubbleProps) {
  const { t } = useI18n()
  switch (message.status) {
    case "searching":
      return (
        <div className="flex flex-col gap-1 py-0.5 text-sm">
          <div className="flex items-center gap-2">
            <Loader2 className="size-3.5 animate-spin text-orange" />
            <span className="text-shimmer">{t("message.searching", { target: message.searchingLabel ?? "SharePoint" })}</span>
          </div>
          {message.searchingDetail?.length ? (
            <ul className="ml-6 flex flex-col gap-0.5 text-xs text-fg3">
              {message.searchingDetail.map((query) => (
                <li key={query} className="line-clamp-2">
                  {query}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )
    case "generating-image":
      return (
        <div className="flex h-40 w-56 animate-pulse items-center justify-center rounded-xl bg-muted">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <ImageIcon className="size-6" />
            <span className="text-xs">{t("message.generatingImage")}</span>
          </div>
        </div>
      )
    case "error":
      // Keep what already streamed: the backend persists the partial text, so
      // hiding it here would make the thread change content on reload.
      return (
        <>
          {message.content ? <Markdown content={message.content} /> : null}
          <div className={cn("text-sm text-destructive", message.content && "mt-3")}>
            <p>{t("message.error")}</p>
            {message.errorMessage && <p className="mt-1 text-xs opacity-80">{message.errorMessage}</p>}
          </div>
        </>
      )
    case "streaming":
      return (
        <div className="streaming-cursor">
          <Markdown content={message.content} />
        </div>
      )
    case "done":
      return (
        <>
          {message.image ? <GeneratedImage url={message.image.url} prompt={message.image.prompt} /> : null}
          {message.content ? <Markdown content={message.content} sources={message.sources} messageId={message.id} /> : null}
          {message.sources && <SourceList sources={message.sources} messageId={message.id} />}
          {message.retrievalNotice && (
            <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-orange/35 bg-orange-tint px-3.5 py-2.5 text-xs text-fg2">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-orange" />
              <span>
                {t("message.answeredWith")} <strong className="font-medium">{retrievalEngineLabel(message.retrievalEngine)}</strong> —{" "}
                {message.retrievalNotice}
              </span>
            </div>
          )}
          {message.personalNotice && (
            <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber/35 bg-amber-tint px-3.5 py-2.5 text-xs text-fg2">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber" />
              <span>{message.personalNotice}</span>
            </div>
          )}
          {message.scopeNotice && (
            <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber/35 bg-amber-tint px-3.5 py-2.5 text-xs text-fg2">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber" />
              <span>{message.scopeNotice}</span>
            </div>
          )}
          {message.lowConfidence && (
            <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-border bg-elevated px-3.5 py-2.5 text-xs text-fg2">
              <Info className="mt-0.5 size-3.5 shrink-0 text-fg3" />
              <span>
                {t("message.lowConfidence")}
              </span>
            </div>
          )}
          {(message.content || message.image) && (
            <div className="mt-3.5 flex items-center gap-1.5">
              {message.content && <CopyButton text={message.content} />}
              {message.agentLabel && <span className="ml-1 text-[11px] text-fg3">{message.agentLabel}</span>}
              {/* Which engine answered — the point of the switch is comparing the
                  two, so this has to stay readable when scrolling back. */}
              {message.retrievalEngine && !message.retrievalNotice && message.sources?.length ? (
                <span className="ml-1 text-[11px] text-fg4">{retrievalEngineLabel(message.retrievalEngine)}</span>
              ) : null}
            </div>
          )}
          {message.suggestions && <SuggestionChips suggestions={message.suggestions} onSelect={onSuggestionSelect} />}
        </>
      )
  }
}

interface MessageBubbleProps {
  message: ChatMessage
  onSuggestionSelect: (question: string) => void
}

export const MessageBubble = memo(function MessageBubble({ message, onSuggestionSelect }: MessageBubbleProps) {
  if (message.role === "user") {
    return (
      <div className="flex animate-in fade-in slide-in-from-bottom-1 justify-end duration-200">
        <div className="flex max-w-[78%] flex-col items-end gap-1.5">
          {message.attachment && (
            <span className="flex items-center gap-1.5 rounded-lg border border-border bg-elevated px-2.5 py-1 text-[11px] text-fg2">
              {message.attachment.type === "image" ? (
                <ImageIcon className="size-3" />
              ) : (
                <FileText className="size-3" />
              )}
              <span className="max-w-52 truncate">{message.attachment.filename}</span>
            </span>
          )}
          <div
            className={cn(
              "rounded-2xl rounded-br-sm bg-user-bubble px-4 py-2.5 text-[15px] leading-relaxed break-words whitespace-pre-wrap text-foreground",
            )}
          >
            {message.content}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex animate-in fade-in slide-in-from-bottom-1 gap-3.5 duration-200">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-foreground text-background select-none">
        <Sparkles className="size-4" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <AssistantBody message={message} onSuggestionSelect={onSuggestionSelect} />
      </div>
    </div>
  )
})
